import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
  AttachFile as AttachIcon,
  Search as SearchIcon,
  LinkOff as UnlinkIcon,
} from "@mui/icons-material";
import {
  MaintenanceInvoiceDto,
  useGetMaintenanceInvoicesQuery,
  useUploadMaintenanceInvoiceMutation,
  useDeleteMaintenanceInvoiceMutation,
} from "../../../api/maintenanceApi";

const PAGE_SIZE = 24;

interface InvoiceGalleryPickerProps {
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  vehicleId?: number | null;
  trailerId?: number | null;
  knownInvoices?: MaintenanceInvoiceDto[];
}

const isImageType = (contentType: string) => contentType.startsWith("image/");

const InvoiceCard: React.FC<{
  invoice: MaintenanceInvoiceDto;
  compact?: boolean;
  attached?: boolean;
  onRemove?: () => void;
  onAttach?: () => void;
  onDelete?: () => void;
}> = ({ invoice, compact, attached, onRemove, onAttach, onDelete }) => (
  <Card
    variant="outlined"
    sx={{
      position: "relative",
      width: compact ? 120 : 140,
      borderColor: attached ? "primary.main" : "divider",
      borderWidth: attached ? 2 : 1,
    }}
  >
    {onDelete && (
      <Tooltip title="Delete invoice">
        <IconButton
          size="small"
          sx={{
            position: "absolute",
            top: 2,
            right: 2,
            zIndex: 1,
            bgcolor: "background.paper",
            "&:hover": { bgcolor: "error.light", color: "error.contrastText" },
          }}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    )}
    {onRemove && (
      <Tooltip title="Unlink">
        <IconButton
          size="small"
          sx={{
            position: "absolute",
            top: 2,
            right: 2,
            zIndex: 1,
            bgcolor: "background.paper",
          }}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <UnlinkIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    )}

    {isImageType(invoice.contentType) ? (
      <CardMedia
        component="img"
        height={compact ? 80 : 100}
        image={invoice.url}
        alt={invoice.originalFileName}
        sx={{ objectFit: "cover", cursor: "pointer" }}
        onClick={() => window.open(invoice.url, "_blank")}
      />
    ) : (
      <Box
        sx={{
          height: compact ? 80 : 100,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "action.hover",
          cursor: "pointer",
        }}
        onClick={() => window.open(invoice.url, "_blank")}
      >
        {invoice.contentType === "application/pdf" ? (
          <PdfIcon sx={{ fontSize: 36, color: "error.main" }} />
        ) : (
          <FileIcon sx={{ fontSize: 36, color: "text.secondary" }} />
        )}
      </Box>
    )}

    <Box sx={{ p: 0.75 }}>
      <Typography variant="caption" noWrap display="block" title={invoice.originalFileName}>
        {invoice.originalFileName}
      </Typography>
      {onAttach && (
        <Button
          size="small"
          fullWidth
          variant={attached ? "contained" : "outlined"}
          sx={{ mt: 0.5, textTransform: "none", fontSize: "0.7rem" }}
          onClick={onAttach}
        >
          {attached ? "Attached" : "Attach"}
        </Button>
      )}
    </Box>
  </Card>
);

const InvoiceGalleryPicker: React.FC<InvoiceGalleryPickerProps> = ({
  selectedIds,
  onChange,
  vehicleId,
  trailerId,
  knownInvoices = [],
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [invoiceCache, setInvoiceCache] = useState<Record<number, MaintenanceInvoiceDto>>({});
  const [uploadInvoice, { isLoading: isUploading }] =
    useUploadMaintenanceInvoiceMutation();
  const [deleteInvoice] = useDeleteMaintenanceInvoiceMutation();

  useEffect(() => {
    if (!knownInvoices.length) return;
    setInvoiceCache((prev) => {
      const next = { ...prev };
      for (const inv of knownInvoices) next[inv.id] = inv;
      return next;
    });
  }, [knownInvoices]);

  const selectedInvoices = useMemo(
    () =>
      selectedIds
        .map((id) => invoiceCache[id])
        .filter((inv): inv is MaintenanceInvoiceDto => !!inv),
    [selectedIds, invoiceCache]
  );

  const [browseOpen, setBrowseOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [browseItems, setBrowseItems] = useState<MaintenanceInvoiceDto[]>([]);

  useEffect(() => {
    if (!browseOpen) return;
    const t = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPageNumber(1);
      setBrowseItems([]);
    }, 300);
    return () => window.clearTimeout(t);
  }, [searchInput, browseOpen]);

  const assetReady = !!(vehicleId || trailerId);

  const {
    data: browsePage,
    isFetching: isBrowseLoading,
    isError: isBrowseError,
  } = useGetMaintenanceInvoicesQuery(
    {
      vehicleId: vehicleId || undefined,
      trailerId: trailerId || undefined,
      pageNumber,
      pageSize: PAGE_SIZE,
      search: search || undefined,
    },
    { skip: !browseOpen || !assetReady }
  );

  useEffect(() => {
    if (!browsePage?.items) return;
    setBrowseItems((prev) => {
      if (pageNumber === 1) return browsePage.items;
      const existing = new Set(prev.map((i) => i.id));
      const additions = browsePage.items.filter((i) => !existing.has(i.id));
      return additions.length === 0 ? prev : [...prev, ...additions];
    });
  }, [browsePage, pageNumber]);

  const rememberInvoice = useCallback((invoice: MaintenanceInvoiceDto) => {
    setInvoiceCache((prev) => ({ ...prev, [invoice.id]: invoice }));
  }, []);

  const handleUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files?.length || !assetReady) return;
      setError(null);

      const nextIds = [...selectedIds];
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append("file", file);
        try {
          const result = await uploadInvoice({
            vehicleId: vehicleId || undefined,
            trailerId: trailerId || undefined,
            formData,
          }).unwrap();
          rememberInvoice(result);
          if (!nextIds.includes(result.id)) nextIds.push(result.id);
        } catch {
          setError(`Failed to upload ${file.name}`);
        }
      }
      onChange(nextIds);

      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [
      uploadInvoice,
      onChange,
      selectedIds,
      rememberInvoice,
      vehicleId,
      trailerId,
      assetReady,
    ]
  );

  const handleUnlink = useCallback(
    (id: number) => {
      onChange(selectedIds.filter((x) => x !== id));
    },
    [onChange, selectedIds]
  );

  const handleAttach = useCallback(
    (invoice: MaintenanceInvoiceDto) => {
      rememberInvoice(invoice);
      if (!selectedIds.includes(invoice.id)) {
        onChange([...selectedIds, invoice.id]);
      }
    },
    [rememberInvoice, selectedIds, onChange]
  );

  const handleDeleteFromGallery = useCallback(
    async (id: number) => {
      setError(null);
      try {
        await deleteInvoice(id).unwrap();
        onChange(selectedIds.filter((x) => x !== id));
        setBrowseItems((prev) => prev.filter((i) => i.id !== id));
        setInvoiceCache((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      } catch (err: any) {
        const message =
          err?.data?.message ||
          err?.data?.errors?.[0] ||
          "Cannot delete invoice (it may still be attached to a record).";
        setError(message);
      }
    },
    [deleteInvoice, onChange, selectedIds]
  );

  const openBrowse = () => {
    if (!assetReady) {
      setError(vehicleId === undefined && trailerId === undefined
        ? "Select a vehicle or trailer first."
        : "Select a vehicle or trailer first.");
      return;
    }
    setBrowseOpen(true);
    setSearchInput("");
    setSearch("");
    setPageNumber(1);
    setBrowseItems([]);
    setError(null);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ letterSpacing: 1 }}
        >
          Invoices
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AttachIcon />}
            onClick={openBrowse}
            disabled={!assetReady}
          >
            Attach existing
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              isUploading ? <CircularProgress size={16} /> : <UploadIcon />
            }
            disabled={isUploading || !assetReady}
            onClick={() => fileInputRef.current?.click()}
          >
            Upload
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.docx,.txt"
            onChange={handleUpload}
          />
        </Box>
      </Box>

      {error && !browseOpen && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mb: 1, display: "block" }}
        >
          {error}
        </Typography>
      )}

      {selectedInvoices.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 2, textAlign: "center" }}
        >
          No invoices attached. Upload a new file or attach an existing one.
        </Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            maxHeight: 280,
            overflowY: "auto",
            py: 0.5,
          }}
        >
          {selectedInvoices.map((inv) => (
            <InvoiceCard
              key={inv.id}
              invoice={inv}
              onRemove={() => handleUnlink(inv.id)}
            />
          ))}
        </Box>
      )}

      {selectedIds.length > 0 && (
        <Typography
          variant="caption"
          color="primary"
          sx={{ mt: 1, display: "block" }}
        >
          {selectedIds.length} invoice(s) attached
        </Typography>
      )}

      <Dialog
        open={browseOpen}
        onClose={() => setBrowseOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Attach existing invoice</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by file name…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {error && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mb: 1, display: "block" }}
            >
              {error}
            </Typography>
          )}

          {isBrowseError && (
            <Typography color="error" variant="body2">
              Failed to load invoices.
            </Typography>
          )}

          {browseItems.length === 0 && !isBrowseLoading ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 3, textAlign: "center" }}
            >
              No invoices found.
            </Typography>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                maxHeight: 360,
                overflowY: "auto",
              }}
            >
              {browseItems.map((inv) => {
                const isSelected = selectedIds.includes(inv.id);
                return (
                  <InvoiceCard
                    key={inv.id}
                    invoice={inv}
                    compact
                    attached={isSelected}
                    onAttach={() =>
                      isSelected ? handleUnlink(inv.id) : handleAttach(inv)
                    }
                    onDelete={() => handleDeleteFromGallery(inv.id)}
                  />
                );
              })}
            </Box>
          )}

          {isBrowseLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {browsePage?.hasNextPage && !isBrowseLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Button
                size="small"
                onClick={() => setPageNumber((p) => p + 1)}
              >
                Load more
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBrowseOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceGalleryPicker;
